import careConfig from "@careConfig";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { navigate } from "raviger";
import { useCallback, useEffect, useState } from "react";

import Loading from "@/components/Common/Loading";

import { AuthUserContext } from "@/hooks/useAuthUser";

import { LocalStorageKeys } from "@/common/constants";

import query from "@/Utils/request/query";
import { userAtom } from "@/atoms/user-atom";
import {
  getKeycloakToken,
  initKeycloak,
  kcLogin,
  kcLogout,
  refreshKeycloakToken,
} from "@/Integrations/Keycloak/keycloak";
import { MfaLoginRequest } from "@/types/auth/auth";
import { TokenData } from "@/types/otp/otp";
import userApi from "@/types/user/userApi";

interface Props {
  children: React.ReactNode;
  unauthorized: React.ReactNode;
  otpAuthorized: React.ReactNode;
}

export default function AuthUserProvider({
  children,
  unauthorized,
  otpAuthorized,
}: Props) {
  const queryClient = useQueryClient();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [keycloakReady, setKeycloakReady] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [patientToken, setPatientToken] = useState<TokenData | null>(
    JSON.parse(
      localStorage.getItem(LocalStorageKeys.patientTokenKey) || "null",
    ),
  );

  // Initialize Keycloak with a silent SSO check. On success, store the access
  // token so the request layer + currentUser query can use it. Never trust a
  // stale token in localStorage before Keycloak confirms authentication.
  useEffect(() => {
    let mounted = true;
    initKeycloak()
      .then((authenticated) => {
        if (!mounted) return;
        if (authenticated && getKeycloakToken()) {
          setAccessToken(getKeycloakToken() ?? null);
        } else {
          localStorage.removeItem(LocalStorageKeys.accessToken);
          localStorage.removeItem(LocalStorageKeys.refreshToken);
        }
      })
      .catch(() => {
        if (mounted) {
          localStorage.removeItem(LocalStorageKeys.accessToken);
          localStorage.removeItem(LocalStorageKeys.refreshToken);
        }
      })
      .finally(() => {
        if (mounted) setKeycloakReady(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const { data: user, isLoading } = useQuery({
    queryKey: ["currentUser", accessToken],
    queryFn: query(userApi.currentUser, { silent: true }),
    retry: false,
    enabled: !!accessToken,
  });

  const setUser = useSetAtom(userAtom);
  useEffect(() => {
    setUser(user);
  }, [user, setUser]);

  // Proactive token refresh: keep the Keycloak access token fresh (the backend
  // verifies `exp`). A refresh does not change the user, so no refetch needed.
  useEffect(() => {
    if (!accessToken) return;
    const id = setInterval(() => {
      void refreshKeycloakToken();
    }, careConfig.auth.tokenRefreshInterval);
    return () => clearInterval(id);
  }, [accessToken]);

  const signIn = useCallback(async () => {
    setIsAuthenticating(true);
    try {
      await kcLogin();
    } catch (error) {
      console.error("Keycloak login failed:", error);
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  // MFA is enforced by Keycloak (realm OTP). care-fe's built-in TOTP flow is
  // retired; this stub exists so the existing 2FA UI still type-checks.
  const verifyMFA = useCallback(async (_data: MfaLoginRequest) => {
    throw new Error("MFA is handled by Keycloak");
  }, []);
  const isVerifyingMFA = false;

  const patientLogin = (tokenData: TokenData, redirectUrl: string) => {
    setPatientToken(tokenData);
    localStorage.setItem(
      LocalStorageKeys.patientTokenKey,
      JSON.stringify(tokenData),
    );
    navigate(redirectUrl);
  };

  const signOut = useCallback(async () => {
    localStorage.removeItem(LocalStorageKeys.accessToken);
    localStorage.removeItem(LocalStorageKeys.refreshToken);
    localStorage.removeItem(LocalStorageKeys.patientTokenKey);
    setAccessToken(null);
    setPatientToken(null);

    await queryClient.resetQueries({ queryKey: ["currentUser"] });

    try {
      await kcLogout();
    } catch (error) {
      console.error("Keycloak logout failed:", error);
    }

    const redirectURL = getRedirectURL();
    navigate(redirectURL ? `/login?redirect=${redirectURL}` : "/login");
  }, [queryClient]);

  // Handles signout from current tab, if signed out from another tab.
  useEffect(() => {
    const listener = (event: StorageEvent) => {
      if (
        !event.newValue &&
        event.key &&
        [
          LocalStorageKeys.accessToken,
          LocalStorageKeys.refreshToken,
          LocalStorageKeys.patientTokenKey,
        ].includes(event.key)
      ) {
        signOut();
      }
    };

    addEventListener("storage", listener);

    return () => {
      removeEventListener("storage", listener);
    };
  }, [signOut]);

  if (!keycloakReady || isLoading) {
    return <Loading />;
  }

  return (
    <AuthUserContext.Provider
      value={{
        signIn,
        signOut,
        verifyMFA,
        isAuthenticating,
        isVerifyingMFA,
        user,
        patientLogin,
        patientToken,
      }}
    >
      {user ? children : patientToken?.token ? otpAuthorized : unauthorized}
    </AuthUserContext.Provider>
  );
}

const getRedirectURL = () => {
  return new URLSearchParams(window.location.search).get("redirect");
};
